<template>
	<v-card
		class="service-endpoint-card"
		:class="{ 'selected': selected }"
		elevation="2"
		hover
		@click="$emit('select')"
	>
		<v-card-text class="d-flex justify-center pa-4">
			<div class="endpoint-card-content">
				<div class="endpoint-icon-section">
					<v-avatar
						size="56"
						:color="endpoint.template.color || 'primary'"
						class="white--text"
					>
						<font-awesome-icon
							v-if="endpoint.template.icon && endpoint.template.icon.startsWith('fa')"
							:icon="getFontAwesomeIcon(endpoint.template.icon)"
							size="2x"
							:style="{ color: endpoint.template.iconColor || 'white' }"
						/>
						<v-icon
							v-else-if="endpoint.template.icon"
							size="32"
							:color="endpoint.template.iconColor || 'white'"
						>
							{{ endpoint.template.icon }}
						</v-icon>
						<span
							v-else
							class="font-weight-bold text-h6"
						>
							{{ endpoint.template.name.charAt(0).toUpperCase() }}
						</span>
					</v-avatar>
				</div>
				<div class="endpoint-content-section">
					<h3 class="endpoint-title text-h6 font-weight-medium mb-1">
						{{ endpoint.name }}
					</h3>
					<p class="endpoint-description text-body-2 text--secondary mb-2">
						{{ endpoint.description || `${endpoint.template.name} endpoint for ${endpoint.name.toLowerCase()}` }}
					</p>
					<div class="endpoint-url-container">
						<span class="endpoint-method">
							{{ endpoint.method }}
						</span>
						<span class="endpoint-path">
							{{ displayUrl }}
						</span>
					</div>
				</div>
				<div
					v-if="selected"
					class="selection-indicator"
				>
					<v-icon
						color="primary"
						size="24"
					>
						mdi-check-circle
					</v-icon>
				</div>
			</div>
		</v-card-text>
	</v-card>
</template>

<script>
import { getFontAwesomeIcon } from '../../../../../shared/utils/get-font-awesome-icon.js'
export default {
	name: 'service-endpoint-card',
	props: {
		endpoint: {
			type: Object,
			required: true
		},
		selected: {
			type: Boolean,
			default: false
		}
	},
	setup () {
		return {
			getFontAwesomeIcon
		}
	},
	computed: {
		displayUrl () {
			// Mirror resolveEndpointPrefill: url-less endpoints (e.g. webhook templates)
			// fall back to the template's defaults.endpointAddress rather than an empty pill.
			return this.endpoint.path ||
				this.endpoint.url ||
				(this.endpoint.template.defaults && this.endpoint.template.defaults.endpointAddress) ||
				''
		}
	},
	methods: {
		getMethodColor (method) {
			const colors = {
				GET: 'blue',
				POST: 'green',
				PUT: 'orange',
				PATCH: 'purple',
				DELETE: 'red'
			}
			return colors[method?.toUpperCase()] || 'grey'
		}
	}
}
</script>

<style scoped>
.service-endpoint-card {
	cursor: pointer;
	transition: all 0.2s ease;
	border: 2px solid transparent;
	background-color: var(--global-main-background-color);
	width: 100%;
	min-height: 230px;
	height: 100%; /* fill parent to align endpoints across cards when parent rows stretch */
	position: relative;
	display: flex;
	flex-direction: column;
}

.service-endpoint-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25) !important;
}

.service-endpoint-card.selected {
	border-color: var(--v-primary-base);
	background-color: rgba(var(--v-primary-base), 0.05);
}

.endpoint-card-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	position: relative;
	height: 100%;
	/* Constrain to the card's width: as a flex child this block otherwise sizes to its
	content's intrinsic width, so a long single-line title pushes it wider than the
	card and the text bleeds into neighbouring cards. */
	width: 100%;
	min-width: 0;
	padding: 16px;
	box-sizing: border-box;
}

.endpoint-icon-section {
	margin-bottom: 16px;
}

.endpoint-content-section {
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	min-height: 120px;
}

.endpoint-title {
	color: var(--global-foreground, rgba(0, 0, 0, 0.87));
	line-height: 1.3;
	text-align: center;
	max-width: 100%;
	overflow-wrap: break-word;
}

.endpoint-description {
	color: var(--global-muted-foreground, rgba(0, 0, 0, 0.6));
	line-height: 1.4;
	text-align: center;
	/* allow content to wrap naturally without truncation or scroll */
	max-width: 90%;
	word-break: break-word;
}

.endpoint-url-container {
	display: flex;
	align-items: center;
	width: 100%;
	max-width: 360px; /* Ensure it fits within the card */
	border-radius: var(--radius-sm, 4px);
	overflow: hidden;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
	border: 1px solid var(--global-border-color, rgba(0, 0, 0, 0.06));
	margin-top: auto; /* pin to bottom of content section */
	margin-bottom: 0;
}

.endpoint-method {
	padding: 4px 8px;
	font-weight: 600;
	font-size: 0.7rem;
	text-transform: uppercase;
	flex-shrink: 0;
	width: 60px;
	text-align: center;
	background-color: var(--global-primary);
	color: white;
}

.endpoint-path {
	font-family: 'SFMono-Regular', 'Consolas', 'Liberation Mono', 'Menlo', monospace;
	color: var(--global-foreground, rgba(0, 0, 0, 0.87));
	padding: 4px 8px;
	font-size: 0.75rem;
	flex-grow: 1;
	background-color: var(--global-card-background, white);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.selection-indicator {
	position: absolute;
	top: 8px;
	right: 8px;
	flex-shrink: 0;
}

/* Ensure the Vuetify card text area fills the card height so the bottom row can pin reliably */
.service-endpoint-card ::v-deep .v-card__text {
	height: 100%;
	display: flex;
}

/* Responsive adjustments */
@media (max-width: 600px) {
	.endpoint-icon {
		margin-right: 12px;
	}

	.endpoint-icon .v-avatar {
		width: 40px !important;
		height: 40px !important;
	}

	.endpoint-title {
		font-size: 1.1rem;
	}

	.endpoint-description {
		font-size: 0.875rem;
	}

	.endpoint-path {
		font-size: 0.8rem;
	}
}
</style>

<!-- Unscoped styles for dark mode support -->
<style lang="scss">
/* Dark mode overrides for service-endpoint-card */
.dark,
.theme--dark {
	.service-endpoint-card.v-card {
		background-color: var(--global-card-background, #1c1c1e) !important;
		border-color: var(--global-border-color, #2e2e32) !important;
	}

	.service-endpoint-card.selected {
		border-color: var(--global-primary, #ed303e) !important;
		background-color: rgba(237, 48, 62, 0.1) !important;
	}

	.service-endpoint-card .endpoint-url-container {
		border-color: var(--global-border-color, #2e2e32) !important;
	}

	.service-endpoint-card .endpoint-path {
		background-color: rgba(255, 255, 255, 0.04) !important;
	}
}
</style>
